import { Search, Bell, Settings, Menu, ChevronDown, Home, X } from 'lucide-react'
import CustomUserMenu from './CustomUserMenu'
import OrganizationHeader from './OrganizationHeader'
import OrganizationSwitcher from './OrganizationSwitcher'
import SearchDropdown from './search/SearchDropdown'
import NexusAgentPopover from './agent/NexusAgentPopover'
import { useQuery } from 'react-query'
import { api } from '../lib/api'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'

interface HeaderProps {
  onOpenSiteSettings: () => void
  onOpenDeletedItems: () => void
  onOpenNotifications: () => void
  isMobile?: boolean
  onToggleSidebar?: () => void
  pageTitle?: string
  hierarchy?: any[]
}

export default function Header({ 
  onOpenSiteSettings, 
  onOpenDeletedItems, 
  onOpenNotifications,
  isMobile = false,
  onToggleSidebar,
  pageTitle,
  hierarchy
}: HeaderProps) {
  // Mobile expandable state
  const [searchExpanded, setSearchExpanded] = useState(false)
  const [breadcrumbExpanded, setBreadcrumbExpanded] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useUser()
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searchTotalCount, setSearchTotalCount] = useState(0)
  const [searchLoading, setSearchLoading] = useState(false)
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const [selectedType, setSelectedType] = useState('all')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  
  // Get current organization ID (from Ghost Mode URL param or user's organization)
  const currentSearchParams = new URLSearchParams(location.search);
  const ghostOrgId = currentSearchParams.get('org');
  const supportSearch = (() => {
    const params = new URLSearchParams();
    ['impersonate', 'org', 'ghost', 'ghostSession'].forEach((key) => {
      const value = currentSearchParams.get(key);
      if (value) params.set(key, value);
    });
    const query = params.toString();
    return query ? `?${query}` : '';
  })();
  const withSupportSearch = useCallback((path: string, extraParams?: Record<string, string>) => {
    const [basePath, existingQuery = ''] = path.split('?');
    const params = new URLSearchParams(existingQuery);
    new URLSearchParams(supportSearch).forEach((value, key) => params.set(key, value));
    Object.entries(extraParams || {}).forEach(([key, value]) => params.set(key, value));
    const query = params.toString();
    return query ? `${basePath}?${query}` : basePath;
  }, [supportSearch]);
  
  // Fetch current user to get their organization ID
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: () => api.get('/users/me').then(res => res.data.data), // Standardize response format
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch onboarding status to check if user has multiple organizations
  const { data: onboardingStatus } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => api.get('/onboarding/status').then(res => res.data.data),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Determine the effective organization ID
  const effectiveOrgId = ghostOrgId || currentUser?.organizationId;
  
  // Fetch most visited pages for quick links
  const { data: quickLinks } = useQuery({
    queryKey: ['quick-links', user?.id],
    queryFn: async () => {
      try {
        const response = await api.get('/settings/user/most_visited_pages?limit=5')
        return response.data.data || []
      } catch (error) {
        console.error('Failed to fetch quick links:', error)
        return []
      }
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
  
  // Load recent searches from localStorage on mount
  useEffect(() => {
    if (user?.id) {
      const stored = localStorage.getItem(`nexus_recent_searches_${user.id}`)
      if (stored) {
        try {
          setRecentSearches(JSON.parse(stored))
        } catch (error) {
          console.error('Failed to parse recent searches:', error)
        }
      }
    }
  }, [user?.id])
  
  // Clear search query when navigating away from search page
  useEffect(() => {
    // Don't clear if we're on the search page
    if (!location.pathname.startsWith('/search')) {
      setSearchQuery('')
      setSearchResults([])
      setSearchTotalCount(0)
      setShowSearchDropdown(false)
    }
  }, [location.pathname])
  
  // Debounced search function
  const performSearch = useCallback(async (query: string, type: string, includeDeleted: boolean) => {
    if (query.length < 3) {
      setSearchResults([])
      setSearchTotalCount(0)
      setShowSearchDropdown(false)
      return
    }
    
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    
    abortControllerRef.current = new AbortController()
    setSearchLoading(true)
    setShowSearchDropdown(true)
    
    try {
      const params = new URLSearchParams({
        q: query,
        pageSize: '10',
        includeDeleted: includeDeleted.toString()
      })
      
      if (type !== 'all') {
        params.append('types', type)
      }
      
      const response = await api.get(`/search?${params.toString()}`, {
        signal: abortControllerRef.current.signal
      })
      
      if (response.data.success) {
        setSearchResults(response.data.data.results)
        setSearchTotalCount(response.data.data.totalCount)
      }
    } catch (error: any) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Search error:', error)
      }
    } finally {
      setSearchLoading(false)
    }
  }, [])
  
  // Handle search input change with debouncing
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value)
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    // Set new timeout for debouncing (300ms)
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value, selectedType, includeDeleted)
    }, 300)
  }, [selectedType, includeDeleted, performSearch])
  
  // Handle type filter change
  const handleTypeChange = useCallback((type: string) => {
    setSelectedType(type)
    if (searchQuery.length >= 3) {
      performSearch(searchQuery, type, includeDeleted)
    }
  }, [searchQuery, includeDeleted, performSearch])
  
  // Handle include deleted change
  const handleIncludeDeletedChange = useCallback((include: boolean) => {
    setIncludeDeleted(include)
    if (searchQuery.length >= 3) {
      performSearch(searchQuery, selectedType, include)
    }
  }, [searchQuery, selectedType, performSearch])
  
  // Handle result click
  const handleResultClick = useCallback((result: any) => {
    // Save to recent searches
    if (searchQuery && user?.id) {
      const stored = JSON.parse(localStorage.getItem(`nexus_recent_searches_${user.id}`) || '[]')
      const updatedSearches = [searchQuery, ...stored.filter((s: string) => s !== searchQuery)].slice(0, 5)
      localStorage.setItem(`nexus_recent_searches_${user.id}`, JSON.stringify(updatedSearches))
      setRecentSearches(updatedSearches)
    }
    
    // Close dropdown
    setShowSearchDropdown(false)
    setSearchQuery('')
    setSearchExpanded(false)
    
    // Handle navigation based on type
    if (result.type === 'page') {
      navigate(withSupportSearch(`/page/${result.id}`))
    } else if (result.type === 'external_link') {
      window.open(result.metadata.url, '_blank')
    } else {
      // For tiles (content, roadmap, form), navigate to parent page and highlight
      if (result.pageId) {
        navigate(withSupportSearch(`/page/${result.pageId}`, { highlight: result.id }))
      }
    }
  }, [searchQuery, user?.id, navigate, withSupportSearch])
  
  // Handle view all results
  const handleViewAll = useCallback(() => {
    // Save to recent searches (avoid duplicates)
    if (searchQuery && user?.id) {
      const stored = JSON.parse(localStorage.getItem(`nexus_recent_searches_${user.id}`) || '[]')
      const updatedSearches = [searchQuery, ...stored.filter((s: string) => s !== searchQuery)].slice(0, 5)
      localStorage.setItem(`nexus_recent_searches_${user.id}`, JSON.stringify(updatedSearches))
      setRecentSearches(updatedSearches)
    }
    
    const params = new URLSearchParams({
      q: searchQuery,
      from: location.pathname
    })
    
    if (selectedType !== 'all') {
      params.append('types', selectedType)
    }
    
    if (includeDeleted) {
      params.append('includeDeleted', 'true')
    }
    
    new URLSearchParams(supportSearch).forEach((value, key) => params.set(key, value))
    navigate(`/search?${params.toString()}`)
    setShowSearchDropdown(false)
    setSearchQuery('')
    setSearchExpanded(false)
  }, [searchQuery, selectedType, includeDeleted, location.pathname, navigate, user?.id, supportSearch])
  
  // Handle recent search click
  const handleRecentSearchClick = useCallback((query: string) => {
    setSearchQuery(query)
    performSearch(query, selectedType, includeDeleted)
  }, [selectedType, includeDeleted, performSearch])
  
  // Handle quick link click
  const handleQuickLinkClick = useCallback((pageId: string) => {
    setShowSearchDropdown(false)
    setSearchQuery('')
    setSearchExpanded(false)
    navigate(withSupportSearch(`/page/${pageId}`))
  }, [navigate, withSupportSearch])
  
  // Close dropdown when clicking outside
  const handleCloseDropdown = useCallback(() => {
    setShowSearchDropdown(false)
  }, [])
  


  if (isMobile) {
    return (
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 relative z-[49]">
        {/* Main compact header */}
        <div className="flex items-center px-2 h-12">
          {/* Left side: Hamburger + Org Logo */}
          <div className="flex items-center space-x-2">
            {onToggleSidebar && (
              <button
                onClick={onToggleSidebar}
                className="p-1 hover:bg-muted rounded-md"
                title="Toggle sidebar"
              >
                <Menu className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
            
            {effectiveOrgId && (
              <button
                onClick={onOpenSiteSettings}
                className="flex items-center hover:bg-muted rounded-md p-1"
                title="Organization profile"
              >
                {onboardingStatus?.hasMultipleOrganizations ? (
                  <OrganizationSwitcher 
                    effectiveOrgId={effectiveOrgId}
                  />
                ) : (
                  <OrganizationHeader 
                    organizationId={effectiveOrgId}
                    className="flex items-center"
                    showName={false}
                  />
                )}
              </button>
            )}
          </div>
          
          {/* Center: Page title with breadcrumb dropdown */}
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center space-x-1">
              {hierarchy && hierarchy.length > 0 && (
                <button
                  onClick={() => setBreadcrumbExpanded(!breadcrumbExpanded)}
                  className="p-1 hover:bg-muted rounded-md"
                  title="Navigation"
                >
                  <Home className="h-4 w-4 text-muted-foreground" />
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </button>
              )}
              <span className="text-sm font-medium truncate max-w-[200px]">
                {pageTitle || 'Loading...'}
              </span>
            </div>
          </div>
          
          {/* Right side: Search + Other icons */}
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setSearchExpanded(!searchExpanded)}
              className="p-1 hover:bg-muted rounded-md"
              title="Search"
            >
              <Search className="h-4 w-4 text-muted-foreground" />
            </button>
            <NexusAgentPopover compact={true} />
            <button
              className="p-1 hover:bg-muted rounded-md"
              onClick={onOpenNotifications}
              title="My Tasks and Notifications"
              aria-label="My Tasks and Notifications"
            >
              <Bell className="h-4 w-4 text-muted-foreground" />
            </button>
            <button 
              className="p-1 hover:bg-muted rounded-md"
              onClick={onOpenSiteSettings}
              title="Settings"
            >
              <Settings className="h-4 w-4 text-muted-foreground" />
            </button>
            <CustomUserMenu 
              onOpenDeletedItems={onOpenDeletedItems}
              onOpenNotifications={onOpenNotifications}
              compact={true}
            />
          </div>
        </div>
        
        {/* Expandable search bar */}
        {searchExpanded && (
          <div className="px-2 pb-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <input
                type="text"
                placeholder="Search pages, content, forms..."
                className="w-full pl-10 pr-4 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                autoFocus
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => setShowSearchDropdown(true)}
              />
              <button
                onClick={() => {
                  setSearchExpanded(false)
                  setShowSearchDropdown(false)
                  setSearchQuery('')
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-muted rounded-md"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
              {showSearchDropdown && (
                <SearchDropdown
                  query={searchQuery}
                  results={searchResults}
                  totalCount={searchTotalCount}
                  loading={searchLoading}
                  onClose={handleCloseDropdown}
                  onResultClick={handleResultClick}
                  onViewAll={handleViewAll}
                  selectedType={selectedType}
                  onTypeChange={handleTypeChange}
                  includeDeleted={includeDeleted}
                  onIncludeDeletedChange={handleIncludeDeletedChange}
                  recentSearches={recentSearches}
                  quickLinks={quickLinks || []}
                  onRecentSearchClick={handleRecentSearchClick}
                  onQuickLinkClick={handleQuickLinkClick}
                />
              )}
            </div>
          </div>
        )}
        
        {/* Expandable breadcrumb navigation */}
        {breadcrumbExpanded && hierarchy && hierarchy.length > 0 && (
          <div className="px-2 pb-2 border-b border-border">
            <div className="flex items-center space-x-1 text-sm">
              <Link
                to={`/${supportSearch}`}
                className="flex items-center space-x-1 text-muted-foreground hover:text-foreground"
              >
                <Home className="h-3 w-3" />
                <span>Home</span>
              </Link>
              {hierarchy.map((item, index) => (
                <div key={item.id} className="flex items-center space-x-1">
                  <ChevronDown className="h-3 w-3 text-muted-foreground rotate-90" />
                  {index === hierarchy.length - 1 ? (
                    <span className="font-medium">{item.title}</span>
                  ) : (
                    <Link
                      to={withSupportSearch(`/page/${item.id}`)}
                      className="text-muted-foreground hover:text-foreground truncate max-w-[100px]"
                    >
                      {item.title}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>
    )
  }

  // Desktop version (unchanged)
  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 relative z-[49]">
      <div className="flex items-center px-4 h-16">
        <div className="flex items-center space-x-4">
          {effectiveOrgId ? (
            onboardingStatus?.hasMultipleOrganizations ? (
              <OrganizationSwitcher 
                effectiveOrgId={effectiveOrgId}
              />
            ) : (
              <OrganizationHeader 
                organizationId={effectiveOrgId}
                className="flex items-center space-x-2"
              />
            )
          ) : (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gray-300 rounded animate-pulse"></div>
              <span className="text-lg font-semibold text-gray-300 animate-pulse">Loading...</span>
            </div>
          )}
        </div>
        
        <div className="flex-1 flex items-center justify-center">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <input
              type="text"
              placeholder="Search pages, content, forms..."
              className="w-full pl-10 pr-4 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => setShowSearchDropdown(true)}
            />
            {showSearchDropdown && (
              <SearchDropdown
                query={searchQuery}
                results={searchResults}
                totalCount={searchTotalCount}
                loading={searchLoading}
                onClose={handleCloseDropdown}
                onResultClick={handleResultClick}
                onViewAll={handleViewAll}
                selectedType={selectedType}
                onTypeChange={handleTypeChange}
                includeDeleted={includeDeleted}
                onIncludeDeletedChange={handleIncludeDeletedChange}
                recentSearches={recentSearches}
                quickLinks={quickLinks || []}
                onRecentSearchClick={handleRecentSearchClick}
                onQuickLinkClick={handleQuickLinkClick}
              />
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <NexusAgentPopover />
          <button
            className="p-2 hover:bg-muted rounded-md"
            onClick={onOpenNotifications}
            title="My Tasks and Notifications"
            aria-label="My Tasks and Notifications"
          >
            <Bell className="h-5 w-5 text-muted-foreground" />
          </button>
          <button 
            className="p-2 hover:bg-muted rounded-md"
            onClick={onOpenSiteSettings}
            title="Site Settings"
          >
            <Settings className="h-5 w-5 text-muted-foreground" />
          </button>
          <CustomUserMenu 
            onOpenDeletedItems={onOpenDeletedItems}
            onOpenNotifications={onOpenNotifications}
          />
        </div>
      </div>
    </header>
  )
}
